import { showCategoryModal } from '@/components/cashflow/categories/CategoryModal';
import { showTagModal } from '@/components/cashflow/tags/TagModal';
import useCategoryOptions from '@/components/cashflow/useCategoryOptions';
import useMerchantSelect from '@/components/cashflow/useMerchantSelect';
import useTagOptions from '@/components/cashflow/useTagOptions';
import { IconButton } from '@/components/common/Button';
import { StringOption } from '@/components/common/FancySelectInput';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import {
    ChevronDown,
    ChevronRight,
    Minus,
    Plus,
    Trash01,
} from '@/components/common/icons';
import InputField from '@/components/common/InputField';
import { LinkText, Text } from '@/components/common/Typography';
import useAutofocus from '@/components/common/useAutofocus';
import {
    Box,
    Collapse,
    FlexProps,
    Spacer,
    useBreakpointValue,
} from '@chakra-ui/react';
import { OnUpdateFunc, SplitBlockProps } from '@common/react/cashflow';
import { forwardRef, useState } from 'react';

const SplitBlock = forwardRef<HTMLDivElement, SplitBlockProps>(
    function SplitBlock(
        {
            split,
            index,
            leftToSplit,
            onUpdate,
            onDelete,
            canDelete,
            isPositiveTransaction,
        },
        ref
    ) {
        const [isExpanded, setIsExpanded] = useState(split.isExpanded);

        return (
            <Box ref={ref} bg="white" borderRadius={8}>
                <FlexRow
                    p={3}
                    gap={4}
                    {...(isExpanded && {
                        borderBottomColor: 'chakra-border-color',
                        borderBottomWidth: 1,
                    })}
                >
                    <FlexRow
                        align="flex-start"
                        minH={FIELD_INPUT_HEIGHT}
                        w={10}
                        mt={1}
                    >
                        <IconButton
                            aria-label={isExpanded ? 'Contract' : 'Expanded'}
                            onClickEvent="Expand/Contract Split Button Clicked"
                            onClick={() => setIsExpanded(!isExpanded)}
                            icon={
                                isExpanded ? <ChevronDown /> : <ChevronRight />
                            }
                            isRound
                            variant="ghost"
                            size="sm"
                            colorScheme="gray"
                        />
                    </FlexRow>
                    <FieldCell
                        flex={1}
                        inputField={
                            <AmountInput
                                autofocus={index === 0}
                                amount={split.amount}
                                leftToSplit={leftToSplit}
                                onUpdate={onUpdate}
                                isPositiveTransaction={isPositiveTransaction}
                            />
                        }
                    />
                    <FieldCell
                        flex={1.5}
                        inputField={
                            <CategoryInput
                                categoryId={split.categoryId}
                                onUpdate={onUpdate}
                            />
                        }
                    />
                    <FieldCell
                        flex={1.5}
                        inputField={
                            <MerchantInput
                                merchantId={split.merchantId}
                                onUpdate={onUpdate}
                            />
                        }
                    />
                    <FlexRow
                        align="flex-start"
                        minH={FIELD_INPUT_HEIGHT}
                        w={10}
                        mt={1}
                    >
                        <IconButton
                            aria-label="Delete"
                            onClickEvent="Delete Split Button Clicked"
                            onClick={() => onDelete(index)}
                            icon={<Trash01 />}
                            isRound
                            variant="ghost"
                            size="sm"
                            colorScheme="red"
                            isDisabled={!canDelete}
                        />
                    </FlexRow>
                </FlexRow>
                <Collapse in={isExpanded} animateOpacity>
                    <FlexRow p={3} gap={4}>
                        <Box w={10} />
                        <FieldCell
                            flex={2.12}
                            inputField={
                                <TagsInput
                                    tagNames={split.tagNames}
                                    onUpdate={onUpdate}
                                />
                            }
                        />
                        <FlexRow gap={1} flex={1.5}>
                            <FlexCol>
                                <Text color="gray.600" fontWeight="medium">
                                    Hide transaction
                                </Text>
                                <Text color="gray.500" fontSize="sm">
                                    Excludes transaction from spending
                                </Text>
                            </FlexCol>

                            <Spacer />
                            <FlexRow mt={1}>
                                <HideTransactionInput
                                    isHidden={split.isHidden}
                                    onUpdate={onUpdate}
                                />
                            </FlexRow>
                        </FlexRow>
                    </FlexRow>
                </Collapse>
            </Box>
        );
    }
);

export default SplitBlock;

const FIELD_INPUT_HEIGHT = 10;

const FieldCell = ({
    inputField,
    ...flexProps
}: {
    inputField: React.ReactNode;
} & FlexProps) => {
    return (
        <FlexCol {...flexProps} minH={FIELD_INPUT_HEIGHT}>
            {inputField}
        </FlexCol>
    );
};

const AmountInput = ({
    amount,
    leftToSplit,
    onUpdate,
    isPositiveTransaction,
    autofocus,
}: {
    amount: number;
    leftToSplit: number;
    onUpdate: OnUpdateFunc;
    isPositiveTransaction: boolean;
    autofocus: boolean;
}) => {
    const [hasAmountChanged, setHasAmountChanged] = useState(false);
    const autofocusRef = useAutofocus();
    const isMediumScreen = useBreakpointValue(
        {
            base: false,
            md: true,
        },
        {
            fallback: 'base',
            ssr: true,
        }
    );

    return (
        <FlexCol gap={1}>
            <InputField
                name="amount"
                type="currency"
                ref={autofocus ? autofocusRef : undefined}
                helpText={
                    hasAmountChanged
                        ? amount === 0
                            ? 'Amount cannot be $0'
                            : leftToSplit < 0
                              ? 'Total split amount exceeds transaction amount'
                              : leftToSplit > 0
                                ? 'Total split amount is less than transaction amount'
                                : undefined
                        : undefined
                }
                inputProps={{
                    variant: 'outline',
                    fontSize: 'md',
                }}
                inputOptions={{
                    min: 0,
                }}
                value={amount}
                onChange={(newAmount) => {
                    setHasAmountChanged(true);
                    onUpdate({ amount: newAmount });
                }}
                {...(isMediumScreen && {
                    iconAddon: isPositiveTransaction ? (
                        <Plus size="sm" />
                    ) : (
                        <Minus size="sm" />
                    ),
                })}
            />
            {leftToSplit > 0 && (
                <LinkText
                    onClick={() => {
                        setHasAmountChanged(true);
                        onUpdate({
                            amount: leftToSplit + amount,
                        });
                    }}
                >
                    Set to remaining amount
                </LinkText>
            )}
        </FlexCol>
    );
};

const MerchantInput = ({
    merchantId,
    onUpdate,
}: {
    merchantId: string | null | undefined;
    onUpdate: OnUpdateFunc;
}) => {
    const {
        handleLoadOptions,
        handleCreateOption,
        merchantValue,
        handleChange,
    } = useMerchantSelect({
        showLogo: true,
        merchantId,
    });

    return (
        <InputField
            type="fancySelect"
            name="merchant"
            fancySelectOptions={{
                isClearable: true,
                defaultOptions: true,
                loadOptions: handleLoadOptions,
                onCreateOption: async (name) => {
                    const merchant = await handleCreateOption(name);

                    if (merchant) {
                        onUpdate({ merchantId: merchant.id });
                    }
                },
                placeholder: 'Add merchant...',
                formatCreateLabel: (inputValue) =>
                    `Create merchant "${inputValue}"`,
            }}
            value={merchantValue}
            onChange={async (option: StringOption) => {
                handleChange(option);
                onUpdate({ merchantId: option?.value || undefined });
            }}
        />
    );
};

const CategoryInput = ({
    categoryId,
    onUpdate,
}: {
    categoryId: string;
    onUpdate: OnUpdateFunc;
}) => {
    const categoryOptions = useCategoryOptions();

    return (
        <InputField
            type="fancySelect"
            name="category"
            fancySelectOptions={{
                isClearable: false,
                fullScreenPlacement: 'right',
                menuWidth: 300,
                cta: {
                    content: 'Create new subcategory',
                    iconName: 'Plus',
                    onClick: () => {
                        showCategoryModal({
                            onSuccess: ({ id }) => {
                                onUpdate({ categoryId: id });
                            },
                        });
                    },
                },
            }}
            value={categoryId}
            onChange={({ value }) => onUpdate({ categoryId: value })}
            options={categoryOptions}
        />
    );
};

const TagsInput = ({
    tagNames,
    onUpdate,
}: {
    tagNames: string[];
    onUpdate: OnUpdateFunc;
}) => {
    const tagOptions = useTagOptions();

    return (
        <InputField
            type="fancySelect"
            name="tags"
            flexGrow={1}
            fancySelectOptions={{
                isClearable: false,
                isMulti: true,
                controlShouldRenderValue: true,
                placeholder: 'Add tags...',
                menuWidth: 300,
                fullScreenPlacement: 'right',
                cta: {
                    content: 'Create new tag',
                    iconName: 'Plus',
                    onClick: () =>
                        showTagModal({
                            onSuccess: async (tag) =>
                                onUpdate({
                                    tagNames: [...tagNames, tag.name],
                                }),
                        }),
                },
            }}
            value={tagNames}
            onChange={async (options: StringOption[]) =>
                onUpdate({
                    tagNames: options.map((option) => option.value),
                })
            }
            options={tagOptions}
        />
    );
};

const HideTransactionInput = ({
    isHidden,
    onUpdate,
}: {
    isHidden: boolean;
    onUpdate: OnUpdateFunc;
}) => (
    <FlexRow align="flex-start" h={FIELD_INPUT_HEIGHT}>
        <InputField
            label=""
            name="hide_transaction"
            type="yesNo"
            value={isHidden}
            inputProps={{ variant: 'base' }}
            onChange={() => onUpdate({ isHidden: !isHidden })}
        />
    </FlexRow>
);
